import { FC, useState, useCallback, useMemo } from 'react';
import dayjs from 'dayjs';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { useTaskCenterStore } from '@frontend/task-center-scope';
import { useControlledField, DatePickerField, Text, FormRow, TimeField, useTooltip } from '@frontend/design-system';
import { timeFormat, pickerDateFormat } from '../../../constants';
import { getLocationTimeZone, getMinDate, getMinTime } from '../../../utils';
import {
  formRowStyle,
  titleContainerStyle,
  inputContainerStyle,
  baseFieldStyle,
  dateFieldStyle,
} from './date-picker-dialog.styles';

interface DatePickerDialogProps {
  label: string;
  placeholder: string;
  onChange: (value: string) => void;
  value: string;
  isRemoveable?: boolean;
  isEditing?: boolean;
  dueDateHasTime: boolean;
  setDueDateHasTime: (value: boolean) => void;
  selectedLocationId?: string;
  trackingIds?: {
    removeButton?: string;
    chipButton?: string;
  };
  focused?: boolean;
}

export const DatePickerDialog: FC<DatePickerDialogProps> = ({
  label,
  placeholder,
  onChange,
  value,
  selectedLocationId,
  dueDateHasTime,
  setDueDateHasTime,
  focused,
}) => {
  const { t } = useTranslation('task-center');
  const { locationsData } = useTaskCenterStore(['locationsData']);
  const timezone = getLocationTimeZone(locationsData, selectedLocationId);
  const [dateValue, setDateValue] = useState(value ? dayjs(value).format(pickerDateFormat) : '');
  const [timeValue, setTimeValue] = useState(dueDateHasTime ? dayjs(value).format(timeFormat) : '');
  const [minTime, setMinTime] = useState(getMinTime(dateValue ?? '', timezone ?? ''));
  const { Tooltip, triggerProps, tooltipProps } = useTooltip({
    placement: 'top',
    trigger: 'hover',
    buffer: 4,
  });

  const today = useMemo(
    () => (timezone ? dayjs().tz(timezone).format(pickerDateFormat) : dayjs().format(pickerDateFormat)),
    [timezone]
  );

  const minDate = useMemo(() => getMinDate(timezone ?? ''), [timezone]);

  const setDate = useCallback(
    (date: string) => {
      const newMinTime = getMinTime(date, timezone ?? '');
      const updatedDate = date.replaceAll('/', '-');
      if (minTime !== newMinTime) {
        setMinTime(newMinTime);
      }
      if (dueDateHasTime) {
        setDueDateHasTime(false);
      }
      const [_, time] = value.split('T');
      const newDate = [updatedDate, time].join('T');
      setDateValue(updatedDate);
      onChange(newDate);
    },
    [timezone, minTime, dueDateHasTime, onChange, setDueDateHasTime, value]
  );

  const setTime = useCallback(
    (time: string) => {
      const [hour, minute, seconds] = time.split(':');

      const updatedDate = `${dateValue}T${time}`;
      if (!dueDateHasTime) {
        setDueDateHasTime(true);
      }
      setTimeValue(`${hour}:${minute}:${seconds}`);
      onChange(updatedDate);
    },
    [dateValue, dueDateHasTime, onChange, setDueDateHasTime]
  );

  const dateFormField = useControlledField({
    type: 'date',
    placeholder,
    value: dateValue,
    onChange: setDate,
    touched: focused,
  });
  const timeFormField = useControlledField({
    type: 'time',
    placeholder: t('Select Time'),
    value: timeValue,
    onChange: setTime,
  });

  return (
    <FormRow css={formRowStyle} cols={[40]}>
      <div css={titleContainerStyle}>
        <Icon name='calendar' size={16} />
        <Text size='medium' weight='bold'>
          {label}
        </Text>
        <span {...triggerProps}>
          <Icon name='info' size={16} color='light' />
          <Tooltip {...tooltipProps}>
            {t('Adding a due date and time ensures automated reminders a day before and another at the defined time.')}
          </Tooltip>
        </span>
      </div>
      <div css={inputContainerStyle}>
        <DatePickerField
          label=''
          name='Due Date'
          minDate={minDate}
          css={[baseFieldStyle, dateFieldStyle]}
          disabled={!timezone}
          today={today}
          hideErrorText
          {...dateFormField}
        />
        <TimeField
          disabled={!dateValue}
          label=''
          name='Due Time'
          showAdornment
          css={baseFieldStyle}
          timezone={timezone}
          minTime={minTime}
          {...timeFormField}
        />
      </div>
    </FormRow>
  );
};
